import { Icon } from "@iconify/react";
import { Form, Formik, FormikProps, FieldArray, Field, getIn } from "formik";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import _ from "lodash";
import CreatableSelect from "react-select/creatable";
import { Dialog } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  CheckBoxWithLabel,
  Input,
  Label,
  TextArea,
} from "../../components/shared/InputField";
import FileInput from "../../components/shared/FileInput";
import { Select } from "../../components/shared/Select";
import {
  Box,
  ButtonPrimary,
  ButtonSecondary,
  Center,
  Error,
  HStack,
  VStack,
} from "../../components/utils";
import {
  useEditEmployee,
  useEmployeePageDetails,
} from "../../queries/employees";
import { useError } from "../../store/errorStore";
import { usePermissionStore } from "../../store/permissionStore";
import {
  AddEmployeeReq,
  EmployementStatus,
  EmployementType,
} from "../../types/Employee";
import { determineUserAccessToResource } from "../../utils/auth";
import convertToBase64 from "../../utils/convertToBase64";
import { formatDate, formatWithTimeZone } from "../../utils/date";
import useIsMobile from "../../utils/detectDevice";
import {
  Action as DefaultAction,
  Resource,
} from "../../utils/interfaces/Companies";
import { DematAccount } from "../../types/VestingTemplate";
import { employeeTagOptions } from "../../modals/AddEmployee";

interface DematAccountDetails {
  isOpen: boolean;
  dematAccountDetails: DematAccount[];
}

function Profile() {
  const { permission } = usePermissionStore();

  const [disableState, setDisabledState] = useState(false);
  const { id } = useParams();
  const _id = id ?? "";
  const errorMessage = useError();
  const [dematAccountIds, setDematAccountIds] = useState<DematAccountDetails>({
    isOpen: false,
    dematAccountDetails: [],
  });
  const { data: employeePageDetail } = useEmployeePageDetails(_id);
  useEffect(() => {
    if (
      employeePageDetail?.status &&
      [
        EmployementStatus.RESIGNED,
        EmployementStatus.TERMINATED,
        EmployementStatus.RETIRED,
      ].includes(employeePageDetail.status)
    )
      setDisabledState(true);
  }, [employeePageDetail]);
  useEffect(() => {
    setImageUploadURL("");
  }, [id]);

  useEffect(() => {
    if (dematAccountIds.isOpen) return;
    if (!employeePageDetail) return;
    setDematAccountIds((prev) => ({
      ...prev,
      dematAccountDetails: employeePageDetail?.dematAccounts || [],
    }));
  }, [employeePageDetail]);

  const { mutate: addEmployee, isLoading } = useEditEmployee();
  const [imageUploadURL, setImageUploadURL] = useState(
    employeePageDetail && employeePageDetail?.imageURL
  );
  const initialValues: AddEmployeeReq = {
    employeeName: employeePageDetail?.employeeName ?? "",
    employeeDesignation: employeePageDetail?.designation ?? "",
    contactNumber: employeePageDetail?.contactNumber ?? "",
    department: employeePageDetail?.department ?? "",
    dateOfJoin: formatDate(
      new Date(employeePageDetail?.dateOfJoin || "2001-1-1").toUTCString() ||
        new Date("2001-1-1").toUTCString()
    ),
    imageURL: employeePageDetail?.imageURL,
    pan: employeePageDetail?.pan ?? "",
    id: employeePageDetail?.id ?? "",
    address: employeePageDetail?.address ?? "",
    email: employeePageDetail?.emailAddress ?? "",
    manager: employeePageDetail?.manager ?? "",
    residentialStatus: employeePageDetail?.residentStatus ?? "",
    legalName: employeePageDetail?.employeeName ?? "",
    nationality: employeePageDetail?.nationality ?? "",
    grade: employeePageDetail?.grade ?? "",
    employeeIdentification: employeePageDetail?.employeeIdentification ?? "",
    employmentStatus: employeePageDetail?.status ?? EmployementStatus.EMPLOYED,
    company: employeePageDetail?.employerName ?? "",
    employmentType:
      employeePageDetail?.employmentType ?? EmployementType.EMPLOYEE,
    resignationDate: employeePageDetail?.resignationDate
      ? formatDate(employeePageDetail?.resignationDate)
      : undefined,
    lastDay:
      employeePageDetail?.status === EmployementStatus.RESIGNED
        ? formatDate(employeePageDetail?.lastDay) || undefined
        : undefined,
    notes: employeePageDetail?.notes ?? "",
    personalEmail: employeePageDetail?.personalEmail ?? "",
    dematAccounts: employeePageDetail?.dematAccounts ?? [],
    tags: employeePageDetail?.additionalData?.tags || [],
  };

  const validationSchemaParent = () =>
    Yup.object().shape({
      employeeName: disableState
        ? Yup.string()
        : Yup.string().required("Name is required").min(3, "Minimum 3 letters"),
      contactNumber: Yup.string()
        .min(8, "Minimum 8 digits are required")
        .matches(
          /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/im,
          "Invalid Contact Number"
        ),
      dateOfJoin: disableState
        ? Yup.string()
        : Yup.string().required("Date of joining is required"),
      employeeIdentification: disableState
        ? Yup.string()
        : Yup.string().required("Employee Identification ID is required"),
      employeeDesignation: disableState
        ? Yup.string()
        : Yup.string().required("Designation is required"),
      department: disableState
        ? Yup.string()
        : Yup.string().required("Department is required"),
      employmentType: disableState
        ? Yup.string()
        : Yup.string().required("Employment Type is required"),
    });

  const ValidationSchemaChild = () =>
    Yup.object().shape({
      dematAccounts: Yup.array().of(
        Yup.object().shape({
          dematAccountId: Yup.string()
            .required("Demat Account ID is required")
            .matches(
              /^[A-Za-z0-9]{16}$/,
              "Demat Account ID must be 16 characters"
            )
            .test("valid-format", "Invalid Demat Account ID", (value) => {
              if (!value) return false;
              const isNSDL = /^IN\d{14}$/.test(value);
              const isCDSL = /^\d{16}$/.test(value);
              return isNSDL || isCDSL;
            }),
          type: Yup.string()
            .required("Type is required")
            .test(
              "match-type",
              "Type must match the format of the Demat Account ID (NSDL/CDSL)",
              function matchTypeTest(typeValue) {
                const { dematAccountId } = this.parent;
                if (!dematAccountId) return false;
                if (/^IN\d{14}$/.test(dematAccountId)) {
                  return typeValue === "NSDL";
                } else if (/^\d{16}$/.test(dematAccountId)) {
                  return typeValue === "CDSL";
                }
                return false;
              }
            ),
          status: Yup.string().required("Status is required"),
        })
      ),
    });

  const handleFormSubmit = (values: AddEmployeeReq) => {
    values.dematAccounts = dematAccountIds.dematAccountDetails;
    const editEmployeeDto = { ...values };
    editEmployeeDto.dateOfJoin = formatWithTimeZone(editEmployeeDto.dateOfJoin);
    if (
      editEmployeeDto.employmentStatus === EmployementStatus.RESIGNED &&
      editEmployeeDto.lastDay !== undefined
    ) {
      editEmployeeDto.lastDay = formatWithTimeZone(
        editEmployeeDto.lastDay || "2001-1-1"
      );
    }
    editEmployeeDto.email = editEmployeeDto.email.trim();
    editEmployeeDto.dematAccounts = editEmployeeDto.dematAccounts?.map(
      (account) => ({
        ...account,
        s3Path: undefined,
      })
    );
    addEmployee(editEmployeeDto, {
      onSuccess: () => {
        toast("Employee Details Edited Successfully!", { type: "success" });
      },
      onError: (err: any) => {
        errorMessage.setMessage(err.response.data.reason);
        toast(err.response.data.reason, { type: "error", autoClose: 2000 });
      },
    });
  };
  async function handleEditImage(
    e: React.ChangeEvent<HTMLInputElement>,
    formik: FormikProps<AddEmployeeReq>
  ) {
    if (!e?.target?.files?.[0]) return;
    const file = e.target.files[0];
    const base64: string = (await convertToBase64(file, true)) as string;
    setImageUploadURL(base64);
    formik.setFieldValue("imageURL", base64);
  }

  const onCloseBtnClick = () => {
    setDematAccountIds({
      isOpen: false,
      dematAccountDetails: dematAccountIds.dematAccountDetails,
    });
  };

  const onSaveDematAccounts = (accounts: DematAccount[]) => {
    setDematAccountIds({
      isOpen: false,
      dematAccountDetails: accounts,
    });
  };

  const { isMobile } = useIsMobile();
  return (
    <>
      <Box className="my-6 bg-white">
        <HStack className="items-baseline justify-between p-6">
          <Box className="py-2 text-lg font-semibold text-gray-600 ">
            Profile Details
          </Box>
        </HStack>
        <Box className="w-full border-b"></Box>
        <Formik
          initialValues={initialValues}
          enableReinitialize={true}
          onSubmit={(values) => {
            handleFormSubmit(values);
          }}
          validationSchema={validationSchemaParent}
        >
          {(formik) => (
            <>
              <Form>
                <Box
                  className={`${
                    isMobile
                      ? "m-6"
                      : "flex-1 m-6 flex-row md:justify-start md:flex"
                  }`}
                >
                  <Label className="justify-start px-5 py-2 text-sm font-medium text-gray-600 min-w-min w-60">
                    Avatar
                  </Label>
                  <VStack className="gap-4">
                    <Box className="relative w-32">
                      <Label>
                        <input
                          type="file"
                          accept="image/png, image/jpg, image/jpeg"
                          className="hidden"
                          disabled={disableState}
                          onChange={(e) => handleEditImage(e, formik)}
                        />
                        <Icon
                          icon={"eva:edit-outline"}
                          className={`${
                            disableState ? "cursor-not-allowed" : ""
                          } absolute z-10 w-6 h-6 text-gray-400 bg-gray-100 rounded-full cursor-pointer -right-3 hover:text-black`}
                        />
                      </Label>
                      {formik.values.imageURL === null ||
                      formik.values.imageURL === "" ? (
                        <Center className="relative w-32 h-32 bg-gray-100 ">
                          <Center className="flex w-32 h-32 rounded-full ">
                            <p className="text-base font-bold ">
                              {formik.values.employeeName.charAt(0)}
                            </p>
                          </Center>
                        </Center>
                      ) : (
                        <img
                          className="block w-32 h-32 space-y-3 bg-gray-400 border-2 rounded-lg"
                          src={formik.values.imageURL}
                          alt="avatar"
                        />
                      )}

                      <Icon
                        icon={"eva:close-fill"}
                        onClick={() => {
                          setImageUploadURL("");
                          formik.setFieldValue("imageURL", "");
                        }}
                        className={`${
                          disableState ? "cursor-not-allowed" : ""
                        } absolute z-10 w-6 h-6 text-gray-400 bg-gray-100 rounded-full cursor-pointer -right-3 -bottom-3 hover:text-black`}
                      />
                    </Box>
                    <Box className="text-gray-400">
                      Allowed file types: png, jpg, jpeg.
                    </Box>
                  </VStack>
                </Box>
                <Box
                  className={`${
                    isMobile
                      ? "m-6"
                      : "flex-1 m-6 flex-row md:justify-start md:flex"
                  }`}
                >
                  <Label className="px-5 py-2 text-sm font-medium text-gray-600 min-w-min w-60">
                    Name
                  </Label>
                  <Box>
                    <Input
                      className={`${isMobile ? "" : "w-96"} ${
                        disableState ? "cursor-not-allowed" : ""
                      } text-xs1`}
                      type="text"
                      disabled={disableState}
                      placeholder="Enter Name"
                      {...formik.getFieldProps("employeeName")}
                    />
                    {formik.touched.employeeName &&
                      formik.errors.employeeName && (
                        <Error text={formik.errors.employeeName} />
                      )}
                  </Box>
                </Box>
                <Box
                  className={`${
                    isMobile
                      ? "m-6"
                      : "flex-1 m-6 flex-row md:justify-start md:flex"
                  }`}
                >
                  <Label className="px-5 py-2 text-sm font-medium text-gray-600 min-w-min w-60">
                    Email ID
                  </Label>
                  <Box>
                    <Input
                      className={`${isMobile ? "" : "w-96"} text-xs1`}
                      type="text"
                      placeholder="Email ID"
                      {...formik.getFieldProps("email")}
                    />
                    {formik.touched.email && formik.errors.email && (
                      <Error text={formik.errors.email} />
                    )}
                  </Box>
                </Box>
                <Box
                  className={`${
                    isMobile
                      ? "m-6"
                      : "flex-1 m-6 flex-row md:justify-start md:flex"
                  }`}
                >
                  <Label className="px-5 py-2 text-sm font-medium text-gray-600 min-w-min w-60">
                    Personal Email ID
                  </Label>
                  <Box>
                    <Input
                      className={`${isMobile ? "" : "w-96"} text-xs1`}
                      type="email"
                      placeholder="Personal Email ID"
                      {...formik.getFieldProps("personalEmail")}
                    />
                    {formik.touched.personalEmail &&
                      formik.errors.personalEmail && (
                        <Error text={formik.errors.personalEmail} />
                      )}
                  </Box>
                </Box>
                <Box
                  className={`${
                    isMobile
                      ? "m-6"
                      : "flex-1 m-6 flex-row md:justify-start md:flex"
                  }`}
                >
                  <Label className="px-5 py-2 text-sm font-medium text-gray-600 min-w-min w-60">
                    Mobile
                  </Label>
                  <Box>
                    <Input
                      className={`${isMobile ? "" : "w-96"} ${
                        disableState ? "cursor-not-allowed" : ""
                      } text-xs1`}
                      type="text"
                      disabled={disableState}
                      placeholder="Contact Number"
                      {...formik.getFieldProps("contactNumber")}
                    />
                    {formik.touched.contactNumber &&
                      formik.errors.contactNumber && (
                        <Error text={formik.errors.contactNumber} />
                      )}
                  </Box>
                </Box>
                <Box
                  className={`${
                    isMobile
                      ? "m-6"
                      : "flex-1 m-6 flex-row md:justify-start md:flex"
                  }`}
                >
                  <Label className="px-5 py-2 text-sm font-medium text-gray-600 min-w-min w-60">
                    PAN
                  </Label>
                  <Input
                    className={`${isMobile ? "" : "w-96"} ${
                      disableState ? "cursor-not-allowed" : ""
                    } text-xs1 `}
                    type="text"
                    disabled={disableState}
                    placeholder="PAN Number"
                    {...formik.getFieldProps("pan")}
                  />
                </Box>

                <Box className="m-6 md:flex md:flex-row md:items-start">
                  <Label className="px-5 py-2 text-sm font-medium text-gray-600 min-w-min w-60">
                    Demat Account IDs
                  </Label>

                  <Box className="flex-1">
                    <div className="flex items-center w-96 gap-2">
                      <div className={"form-input w-full text-xs1"}>
                        {dematAccountIds?.dematAccountDetails.length} accounts
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const duplicateDemats: DematAccount[] = _.cloneDeep(
                            dematAccountIds?.dematAccountDetails
                          );
                          setDematAccountIds({
                            isOpen: true,
                            dematAccountDetails: duplicateDemats,
                          });
                        }}
                        title="Edit"
                        className={
                          "text-orange-501 hover:scale-110 transition-transform"
                        }
                      >
                        <Icon icon="akar-icons:pencil" width="18" height="18" />
                      </button>
                    </div>
                  </Box>
                </Box>

                <Box
                  className={`${
                    isMobile
                      ? "m-6"
                      : "flex-1 m-6 flex-row md:justify-start md:flex"
                  }`}
                >
                  <Label className="px-5 py-2 text-sm font-medium text-gray-600 min-w-min w-60">
                    Emp ID
                  </Label>
                  <Box>
                    <Input
                      className={`${isMobile ? "" : "w-96"} ${
                        disableState ? "cursor-not-allowed" : ""
                      } text-xs1`}
                      type="text"
                      placeholder="Employee ID"
                      disabled={disableState}
                      {...formik.getFieldProps("employeeIdentification")}
                    />
                    {formik.touched.employeeIdentification &&
                      formik.errors.employeeIdentification && (
                        <Error text={formik.errors.employeeIdentification} />
                      )}
                  </Box>
                </Box>
                <Box
                  className={`${
                    isMobile
                      ? "m-6"
                      : "flex-1 m-6 flex-row md:justify-start md:flex"
                  }`}
                >
                  <Label className="px-5 py-2 text-sm font-medium text-gray-600 min-w-min w-60">
                    Employee Type
                  </Label>
                  <Box>
                    <Select
                      className={`text-xs1 ${isMobile ? "" : "w-96"}
                      ${disableState ? "cursor-not-allowed" : ""}`}
                      options={["EMPLOYEE", "CONSULTANT"]}
                      isPlaceHolderDisabled={true}
                      disabled={disableState}
                      {...formik.getFieldProps("employmentType")}
                    />
                    {formik.touched.employmentType &&
                      formik.errors.employmentType && (
                        <Error text={formik.errors.employmentType} />
                      )}
                  </Box>
                </Box>

                <Box
                  className={`${
                    isMobile
                      ? "m-6"
                      : "flex-1 m-6 flex-row md:justify-start md:flex"
                  }`}
                >
                  <Label className="px-5 py-2 text-sm font-medium text-gray-600 min-w-min w-60">
                    Date of Joining
                  </Label>
                  <Box>
                    <Input
                      className={`${isMobile ? "" : "w-96"} ${
                        disableState ? "cursor-not-allowed" : ""
                      } text-xs1`}
                      type="date"
                      disabled={disableState}
                      placeholder="Select Date"
                      {...formik.getFieldProps("dateOfJoin")}
                    />
                    {formik.touched.dateOfJoin && formik.errors.dateOfJoin && (
                      <Error text={formik.errors.dateOfJoin} />
                    )}
                  </Box>
                </Box>
                {employeePageDetail?.status === EmployementStatus.RESIGNED && (
                  <Box
                    className={`${
                      isMobile
                        ? "m-6"
                        : "flex-1 m-6 flex-row md:justify-start md:flex"
                    }`}
                  >
                    <Label className="px-5 py-2 text-sm font-medium text-gray-600 min-w-min w-60">
                      Resignation Date
                    </Label>
                    <Box>
                      <Input
                        className={`${isMobile ? "" : "w-96"} ${
                          disableState ? "cursor-not-allowed" : ""
                        } text-xs1`}
                        type="date"
                        disabled={disableState}
                        placeholder="Enter Resignation Date"
                        {...formik.getFieldProps("resignationDate")}
                      />
                      {formik.touched.resignationDate &&
                        formik.errors.resignationDate && (
                          <Error text={formik.errors.resignationDate} />
                        )}
                    </Box>
                  </Box>
                )}
                {employeePageDetail?.status === EmployementStatus.RESIGNED && (
                  <Box
                    className={`${
                      isMobile
                        ? "m-6"
                        : "flex-1 m-6 flex-row md:justify-start md:flex"
                    }`}
                  >
                    <Label className="px-5 py-2 text-sm font-medium text-gray-600 min-w-min w-60">
                      Last Working day
                    </Label>
                    <Box>
                      <Input
                        className={` ${isMobile ? "" : "w-96"} ${
                          disableState ? "cursor-not-allowed" : ""
                        } text-xs1`}
                        type="date"
                        disabled={disableState}
                        placeholder="Select Date"
                        {...formik.getFieldProps("lastDay")}
                      />
                      {formik.touched.lastDay && formik.errors.lastDay && (
                        <Error text={formik.errors.lastDay} />
                      )}
                    </Box>
                  </Box>
                )}
                <Box
                  className={`${
                    isMobile
                      ? "m-6"
                      : "flex-1 m-6 flex-row md:justify-start md:flex"
                  }`}
                >
                  <Label className="px-5 py-2 text-sm font-medium text-gray-600 min-w-min w-60">
                    Designation
                  </Label>
                  <Box>
                    <Input
                      className={` ${isMobile ? "" : "w-96"} ${
                        disableState ? "cursor-not-allowed" : ""
                      } text-xs1`}
                      type="text"
                      disabled={disableState}
                      placeholder="Employee Designation"
                      {...formik.getFieldProps("employeeDesignation")}
                    />
                    {formik.touched.employeeDesignation &&
                      formik.errors.employeeDesignation && (
                        <Error text={formik.errors.employeeDesignation} />
                      )}
                  </Box>
                </Box>
                <Box
                  className={`${
                    isMobile
                      ? "m-6"
                      : "flex-1 m-6 flex-row md:justify-start md:flex"
                  }`}
                >
                  <Label className="px-5 py-2 text-sm font-medium text-gray-600 min-w-min w-60">
                    Department
                  </Label>
                  <Box>
                    <Input
                      className={` ${isMobile ? "" : "w-96"} ${
                        disableState ? "cursor-not-allowed" : ""
                      } text-xs1`}
                      type="text"
                      disabled={disableState}
                      placeholder="Enter Department Name"
                      {...formik.getFieldProps("department")}
                    />
                    {formik.touched.department && formik.errors.department && (
                      <Error text={formik.errors.department} />
                    )}
                  </Box>
                </Box>
                <Box
                  className={`${
                    isMobile
                      ? "m-6"
                      : "flex-1 m-6 flex-row md:justify-start md:flex"
                  }`}
                >
                  <Label className="px-5 py-2 text-sm font-medium text-gray-600 min-w-min w-60">
                    Notes
                  </Label>
                  <Box>
                    <TextArea
                      className={` ${isMobile ? "" : "w-96"} ${
                        disableState ? "cursor-not-allowed" : ""
                      } text-xs1`}
                      placeholder="Note"
                      {...formik.getFieldProps("notes")}
                    />
                    {formik.touched.notes && formik.errors.notes && (
                      <Error text={formik.errors.notes} />
                    )}
                  </Box>
                </Box>
                <Box
                  className={`${
                    isMobile
                      ? "m-6"
                      : "flex-1 m-6 flex-row md:justify-start md:flex"
                  }`}
                >
                  <Label className="px-5 py-2 text-sm font-medium text-gray-600 min-w-min w-60">
                    Comments
                  </Label>
                  <Box>
                    <TextArea
                      disabled
                      className={` ${
                        isMobile ? "" : "w-96"
                      } ${"cursor-not-allowed"} text-xs1`}
                      placeholder="Additional Data"
                      value={employeePageDetail?.additionalData?.transferAccessData?.join(
                        "\n\n"
                      )}
                    />
                  </Box>
                </Box>
                <Box
                  className={`${
                    isMobile
                      ? "m-6"
                      : "flex-1 m-6 flex-row md:justify-start md:flex"
                  }`}
                >
                  <Label className="px-5 py-2 text-sm font-medium text-gray-600 min-w-min w-60">
                    Tags
                  </Label>
                  <Box>
                    <CreatableSelect
                      className="w-96"
                      isMulti
                      placeholder="Type anything and press enter..."
                      options={employeeTagOptions}
                      value={formik.values.tags}
                      onChange={(selected) =>
                        formik.setFieldValue("tags", selected)
                      }
                    />
                  </Box>
                </Box>
                <Box className="w-full border-b"></Box>
                <Box
                  className={`${
                    isMobile ? "justify-center" : "justify-end"
                  } flex flex-row `}
                >
                  <ButtonSecondary
                    className={
                      !determineUserAccessToResource(
                        permission?.aclList || [],
                        Resource.Employee,
                        DefaultAction.Edit
                      )
                        ? "opacity-50 cursor-not-allowed m-2"
                        : "m-2"
                    }
                    disabled={
                      !determineUserAccessToResource(
                        permission?.aclList || [],
                        Resource.Employee,
                        DefaultAction.Edit
                      )
                    }
                    onClick={() => formik.resetForm()}
                  >
                    Discard
                  </ButtonSecondary>
                  <ButtonPrimary
                    type="submit"
                    className={
                      !determineUserAccessToResource(
                        permission?.aclList || [],
                        Resource.Employee,
                        DefaultAction.Edit
                      )
                        ? "opacity-50 cursor-not-allowed m-2"
                        : "m-2"
                    }
                    disabled={
                      !determineUserAccessToResource(
                        permission?.aclList || [],
                        Resource.Employee,
                        DefaultAction.Edit
                      )
                    }
                    loading={isLoading}
                  >
                    Save changes
                  </ButtonPrimary>
                </Box>
              </Form>
            </>
          )}
        </Formik>
        <Box
          className={`p-8 bg-white border border-borderColor drop-shadow-box rounded-lg ${
            isMobile ? "min-w-[1040px]" : "min-w-min"
          }`}
        >
          <DematAccountsDialog
            isOpen={dematAccountIds?.isOpen}
            dematAccountDetails={dematAccountIds?.dematAccountDetails || []}
            onCloseBtn={onCloseBtnClick}
            onSaveClickBtn={onSaveDematAccounts}
            ValidationSchemaChild={ValidationSchemaChild}
          />
        </Box>
      </Box>
    </>
  );
}
export default Profile;

interface DematAccountsDialogProps {
  isOpen: boolean;
  dematAccountDetails: DematAccount[];
  onSaveClickBtn: (accounts: DematAccount[]) => void;
  onCloseBtn: () => void;
  ValidationSchemaChild: any;
}

const DematAccountsDialog = ({
  isOpen,
  dematAccountDetails,
  onSaveClickBtn,
  onCloseBtn,
  ValidationSchemaChild,
}: DematAccountsDialogProps) => {
  const resolveUploadedByLabel = (isUserUploaded: boolean | null) => {
    if (isUserUploaded === true) return "Employee";
    if (isUserUploaded === false) return "Admin";
    return "-";
  };

  const resolvePdfUrl = (file?: string) => {
    if (!file) return "";

    const trimmed = file.trim();
    if (!trimmed) return "";

    if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
      return trimmed;
    }

    const base64 = trimmed.startsWith("data:")
      ? trimmed.split(",")[1]
      : trimmed;

    try {
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);

      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }

      const blob = new Blob([bytes], { type: "application/pdf" });
      return URL.createObjectURL(blob);
    } catch (e) {
      return "";
    }
  };

  const openPdfInNewTab = (url: string) => {
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <Dialog open={isOpen} className="w-full h-full" maxWidth="lg">
      <div className="w-[1200px] max-w-[calc(100vw-2rem)] mx-auto bg-white rounded-lg">
        <div className="justify-between px-10 text-lg font-medium border-b py-7">
          <h6 className="flex justify-between">
            Demat Accounts
            <span onClick={onCloseBtn} className="cursor-pointer">
              X
            </span>
          </h6>
        </div>
        <div className="overflow-x-auto px-10">
          <Formik
            initialValues={{
              dematAccounts:
                dematAccountDetails && dematAccountDetails.length > 0
                  ? dematAccountDetails
                  : [],
            }}
            enableReinitialize={true}
            onSubmit={(values) => {
              onSaveClickBtn(values.dematAccounts);
            }}
            validationSchema={ValidationSchemaChild}
          >
            {(formik) => {
              useEffect(() => {
                formik.values.dematAccounts.forEach((account, index) => {
                  const id = account.dematAccountId;
                  let newType = "";

                  if (/^IN\d{14}$/.test(id)) {
                    newType = "NSDL";
                  } else if (/^\d{16}$/.test(id)) {
                    newType = "CDSL";
                  }

                  if (
                    newType &&
                    formik.values.dematAccounts[index].type !== newType
                  ) {
                    formik.setFieldValue(
                      `dematAccounts[${index}].type`,
                      newType
                    );
                  }
                });
              }, [
                formik.values.dematAccounts.map((a) => a.dematAccountId).join(),
              ]);

              return (
                <Form>
                  <VStack className="gap-y-2">
                    {formik.values.dematAccounts.length > 0 && (
                      <div className="mt-4 flex items-center gap-x-8 min-w-[1020px]">
                        <div className="flex-[1.5] min-w-[140px]">
                          <Label className="text-sm font-normal">
                            Demat Account Id
                          </Label>
                        </div>

                        <div className="flex-[0.6] min-w-[120px]">
                          <Label className="text-sm font-normal">Status</Label>
                        </div>

                        <div className="flex-[0.6] min-w-[80px]">
                          <Label className="text-sm font-normal">Type</Label>
                        </div>

                        <div className="flex-none w-[280px]">
                          <Label className="text-sm font-normal">
                            CML File
                          </Label>
                        </div>

                        <div className="flex-[0.9] min-w-[120px]">
                          <Label className="text-sm font-normal">
                            Uploaded by
                          </Label>
                        </div>

                        <div className="w-14 pr-2" />
                      </div>
                    )}

                    {formik.values.dematAccounts.map((account, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-x-8 min-w-[1020px]"
                      >
                        {/* Demat Account Id */}
                        <div className="flex-[1.5] min-w-[140px]">
                          <Input
                            className="text-xs1 w-full h-10"
                            type="text"
                            placeholder="DP ID"
                            {...formik.getFieldProps(
                              `dematAccounts[${index}].dematAccountId`
                            )}
                          />
                          {getIn(
                            formik.touched,
                            `dematAccounts[${index}].dematAccountId`
                          ) &&
                            getIn(
                              formik.errors,
                              `dematAccounts[${index}].dematAccountId`
                            ) && (
                              <Error
                                text={getIn(
                                  formik.errors,
                                  `dematAccounts[${index}].dematAccountId`
                                )}
                              />
                            )}
                        </div>

                        {/* Status */}
                        <div className="flex-[0.6] min-w-[120px]">
                          <Select
                            isPlaceHolderDisabled
                            className="text-xs h-10"
                            options={["active", "inactive"]}
                            valueGetter={(o) => o}
                            textGetter={(o) => o}
                            {...formik.getFieldProps(
                              `dematAccounts[${index}].status`
                            )}
                          />
                          {getIn(
                            formik.touched,
                            `dematAccounts[${index}].status`
                          ) &&
                            getIn(
                              formik.errors,
                              `dematAccounts[${index}].status`
                            ) && (
                              <Error
                                text={getIn(
                                  formik.errors,
                                  `dematAccounts[${index}].status`
                                )}
                              />
                            )}
                        </div>

                        {/* Type */}
                        <div className="flex-[0.6] min-w-[80px]">
                          <Input
                            type="text"
                            disabled
                            value={account.type}
                            className="text-xs1 w-full h-10 bg-gray-100 text-gray-600"
                          />
                        </div>

                        {/* CML File */}
                        <div className="flex-none w-[280px]">
                          <div className="flex items-center w-full min-w-0">
                            <FileInput
                              key={`demat-file-${index}-${
                                account.fileName || account.s3Path || "empty"
                              }`}
                              id={`demat-${index}`}
                              accept="application/pdf"
                              file={account.fileName || "Attach File"}
                              className="flex-1 min-w-0 w-full !max-w-full form-input h-10"
                              onChange={async (e) => {
                                if (!e?.target?.files?.[0]) return;
                                const inputEl = e.currentTarget;
                                const fileData = e.target.files[0];
                                if (fileData.type !== "application/pdf") {
                                  toast("Only PDF files are allowed", {
                                    type: "error",
                                    autoClose: 2000,
                                  });
                                  inputEl.value = "";
                                  return;
                                }

                                const maxBytes = 4 * 1024 * 1024;
                                if (fileData.size > maxBytes) {
                                  toast("File size must be 4MB or less", {
                                    type: "error",
                                    autoClose: 2000,
                                  });
                                  inputEl.value = "";
                                  return;
                                }

                                const base64 = await convertToBase64(fileData);
                                formik.setFieldValue(
                                  `dematAccounts[${index}].file`,
                                  base64 as unknown as string
                                );
                                formik.setFieldValue(
                                  `dematAccounts[${index}].fileName`,
                                  fileData.name
                                );
                                formik.setFieldValue(
                                  `dematAccounts[${index}].fileType`,
                                  "pdf"
                                );
                                formik.setFieldValue(
                                  `dematAccounts[${index}].isUserUploaded`,
                                  false
                                );

                                inputEl.value = "";
                              }}
                            />

                            <button
                              type="button"
                              onClick={() => {
                                let url = resolvePdfUrl(account.file);
                                if (!url) url = (account.s3Path || "").trim();
                                openPdfInNewTab(url);
                              }}
                              title="View document"
                              className="ml-2 text-zinc-300 hover:scale-105"
                              disabled={!(account.file || account.s3Path)}
                            >
                              <Icon icon="ph:eye" width="18" height="18" />
                            </button>

                            <button
                              type="button"
                              onClick={() => {
                                formik.setFieldValue(
                                  `dematAccounts[${index}].file`,
                                  ""
                                );
                                formik.setFieldValue(
                                  `dematAccounts[${index}].fileName`,
                                  ""
                                );
                                formik.setFieldValue(
                                  `dematAccounts[${index}].fileType`,
                                  ""
                                );
                                formik.setFieldValue(
                                  `dematAccounts[${index}].s3Path`,
                                  ""
                                );
                                formik.setFieldValue(
                                  `dematAccounts[${index}].isUserUploaded`,
                                  null
                                );
                                formik.setFieldValue(
                                  `dematAccounts[${index}].cmlDocumentId`,
                                  ""
                                );
                              }}
                              className="ml-2 text-zinc-300 hover:scale-105"
                              title="Remove file"
                              disabled={
                                !(
                                  (account.file ||
                                    account.fileName ||
                                    account.s3Path ||
                                    "") !== ""
                                )
                              }
                            >
                              <DeleteIcon fontSize="small" />
                            </button>
                          </div>
                        </div>

                        {/* Uploaded by */}
                        <div className="flex-[0.9] min-w-[120px]">
                          <Input
                            type="text"
                            disabled
                            value={resolveUploadedByLabel(
                              account.isUserUploaded ?? null
                            )}
                            className="text-xs1 w-full h-10 bg-gray-100 text-gray-600"
                          />
                        </div>

                        {/* Remove Row */}
                        <div className="w-14 flex justify-start">
                          <button
                            type="button"
                            onClick={() => {
                              const arr = [...formik.values.dematAccounts];
                              arr.splice(index, 1);
                              formik.setFieldValue(
                                "dematAccounts",
                                arr.length === 0 ? [] : arr
                              );
                            }}
                            className="ml-2 text-zinc-300 hover:scale-105"
                            title="Remove"
                          >
                            <DeleteIcon fontSize="small" />
                          </button>
                        </div>
                      </div>
                    ))}

                    <div className="flex justify-center items-center px-10 py-6">
                      <ButtonSecondary
                        className="m-2"
                        onClick={() =>
                          formik.setFieldValue("dematAccounts", [
                            ...formik.values.dematAccounts,
                            {
                              dematAccountId: "",
                              status: "active",
                              type: "",
                              file: "",
                              fileName: "",
                              fileType: "",
                              s3Path: "",
                              isUserUploaded: null,
                            },
                          ])
                        }
                      >
                        Add Demat Account
                      </ButtonSecondary>
                    </div>
                  </VStack>

                  {formik.values.dematAccounts.length !== 0 && (
                    <div className="text-xs text-gray-500 mt-2 min-w-[1020px]">
                      <p className="mb-1">
                        * Kindly ensure that the uploaded file is not
                        password-protected.
                      </p>
                      <p>
                        * Changes made here will only be applied after clicking
                        “Save Changes” in your profile.
                      </p>
                    </div>
                  )}
                  <div className="flex justify-end items-center px-10 py-6">
                    <ButtonPrimary
                      onClick={() => formik.handleSubmit()}
                      disabled={!(formik.dirty && formik.isValid)}
                    >
                      Save
                    </ButtonPrimary>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </div>
      </div>
    </Dialog>
  );
};
